import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskViewer, Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEventTask, getCompanyEventDocuments } from 'src/api/company';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/events/$eventId/tasks/$taskId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId, taskId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const { data: task, isLoading: taskLoading } = useQuery({
		queryKey: ['company-event-task', groupId, eventId, taskId],
		queryFn: () => getCompanyEventTask(groupId, eventId, taskId),
		enabled: !userLoading,
	});

	const { data: documents, isLoading: docsLoading } = useQuery({
		queryKey: ['company-event-documents', groupId, eventId],
		queryFn: () => getCompanyEventDocuments(groupId, eventId),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(3, 'Tasks', `/company/${groupId}/events/${eventId}/tasks`);
	useBreadcrumbLevel(4, 'Task Details');

	if (userLoading || taskLoading || docsLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !task) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);

	// Set read-only
	task.executePermission = {
		isAllowed: task.status === 'todo',
		denyReason: task.status === 'todo' ? undefined : 'You are viewing this task in read-only mode',
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['company-event-task', groupId, eventId, taskId] });
		queryClient.invalidateQueries({ queryKey: ['company-event-tasks', groupId, eventId] });
	};

	return (
		<TaskViewer
			task={task}
			documents={documents?.data || []}
			onUpdate={handleUpdate}
			onClose={() =>
				navigate({
					to: '/company/$groupId/events/$eventId/tasks',
					params: { groupId, eventId },
				})
			}
			onOpenDocument={(documentId) => window.open(`/company/${groupId}/events/${eventId}/documents/${documentId}`)}
			permissions={{
				taskDelete: undefined,
				taskDo: verifyPermission(PERMISSIONS.companyEventsTasksDo),
			}}
		/>
	);
}
